import * as React from 'react';
import {connect} from 'react-redux';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {GraphQLErrorObject} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import {clearActiveItem, setIsSidebarFormSaving} from '../../sales-automation/ui-actions';

import styles from './sales-automation-sidebar-form-wrapper.module.css';

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onSaveSuccess: () => {
            dispatch(clearActiveItem());
        },
        onSidebarFormIsSaving: () => {
            dispatch(setIsSidebarFormSaving(true));
        },
        onSidebarFormSaveErrored: () => {
            dispatch(setIsSidebarFormSaving(false));
        },
    };
};

type OwnProps = {
    id: string | null | undefined;
    // Optional because fetching latest version only applies to some forms
    isFetchingLatestVersion?: boolean;
    // Optional because some forms may not be deletable
    onDelete?: {
        deleteConfirmationTitle: string;
        deleteConfirmationSubtitle?: string;
        onDeleteConfirm: (id: string) => Promise<any>;
        deleteText?: string;
    };
    onSaveForm: () => Promise<any> | null | undefined;
    children?: React.ReactNode;
    errorMessage?: string;
};

type DispatchProps = {
    onSaveSuccess: () => void;
    onSidebarFormIsSaving: () => void;
    onSidebarFormSaveErrored: () => void;
};

type Props = OwnProps & DispatchProps;

function SalesAutomationSidebarFormWrapperComponent(props: Props) {
    const [confirmingDeleteId, setConfirmingDeleteId] = React.useState<string | null | undefined>(
        null
    );
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(
        props.errorMessage || null
    );
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    React.useEffect(() => {
        setErrorMessage(props.errorMessage);
    }, [props.errorMessage]);

    const handleSaveForm = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        // If we're fetching the latest version, prevent saving
        if (props.isFetchingLatestVersion) {
            return;
        }

        // Callback passed from the parent component, returns nullable
        // promise, because some forms prevent saving if invalid data.
        const savePromise = props.onSaveForm();
        if (savePromise) {
            // Let redux know our form is saving so we can toggle save state
            props.onSidebarFormIsSaving();

            savePromise
                .then(() => {
                    // Close sidebar
                    props.onSaveSuccess();
                })
                .catch((err: GraphQLErrorObject) => {
                    const message = safelyGetGraphQLErrorMessage(err);
                    setErrorMessage(message);

                    // Clear saving state
                    props.onSidebarFormSaveErrored();
                });
        }
    };

    const handleDelete = () => {
        // As long are we're not fetching the latest version, allow deleting
        if (!props.isFetchingLatestVersion) {
            setConfirmingDeleteId(props.id);
        }
    };

    const onDeleteConfirm = () => {
        if (confirmingDeleteId && props.onDelete) {
            props.onDelete
                .onDeleteConfirm(confirmingDeleteId)
                .then(() => {
                    props.onSaveSuccess();
                })
                .catch((err: GraphQLErrorObject) => {
                    const message = safelyGetGraphQLErrorMessage(err);
                    setErrorMessage(message);
                    setIsDeleting(false);
                });

            // Also set isDeleting state
            setIsDeleting(true);
        }
    };

    const onDeleteCancel = () => {
        setConfirmingDeleteId(null);
    };

    const handleClearError = () => {
        // clear local state error
        setErrorMessage(null);
    };

    const deleteConfirmationTitle =
        props.onDelete && props.onDelete.deleteConfirmationTitle
            ? props.onDelete.deleteConfirmationTitle
            : undefined;

    const deleteConfirmationSubtitle =
        props.onDelete && props.onDelete.deleteConfirmationSubtitle
            ? props.onDelete.deleteConfirmationSubtitle
            : 'This cannot be undone';

    return (
        <div
            className={props.onDelete ? styles['sidebar-form--deleteable'] : styles['sidebar-form']}
        >
            {/* @ts-expect-error */}
            <form onSubmit={handleSaveForm} className={styles['form-container']}>
                {props.children}
            </form>
            {props.id && props.onDelete ? (
                <div className={styles['delete-button-container']}>
                    <Button onClick={handleDelete} variant='danger' size='big' isFullWidth={true}>
                        {isDeleting ? 'Removing…' : props.onDelete.deleteText || 'Remove'}
                    </Button>
                </div>
            ) : undefined}
            {confirmingDeleteId ? (
                <ConfirmationDialog
                    title={deleteConfirmationTitle}
                    message={deleteConfirmationSubtitle}
                    onCancel={onDeleteCancel}
                    onConfirm={onDeleteConfirm}
                />
            ) : undefined}
            {errorMessage ? (
                <FlashBanner isOverlay={true} onHidden={handleClearError} type='error'>
                    <div>{errorMessage}</div>
                </FlashBanner>
            ) : undefined}
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(undefined, mapDispatchToProps);
export const SalesAutomationSidebarFormWrapper = connector(
    SalesAutomationSidebarFormWrapperComponent
);
